// Sprint 17 — single in-flight indicator below the last message.
//
// Renders one of two visible kinds, never both:
//   - thinking → ••• Thinking · 0:08   (three pulsing dots + timer)
//   - tool     → 🔧 Working · 0:14 · <headline>
//
// Layered position: tail of the message list, mutually exclusive with
// `PartialIndicator` (text streaming) — the moment text starts forming
// the parent component swaps this out. See ConversationList.tsx for
// the dispatch.
//
// Founder-first compliance:
// - §3 humanised — `label` is the same string `ToolCallRow` would show
//   for the persisted tool_use, derived from `tool_use.input.description`
//   with the verb-map fallback (computed backend-side in `headline.ts`).
// - §5 actor + verb + state — copy is "Thinking · 0:08" or
//   "Working · 0:14 · <label>". No raw bash, no raw JSON, no token count.
// - §6 zero quantitative metric — only the elapsed wall-clock timer.
//
// Sprint d028-recursive-liveness PR-C (D5, EC-176) — the 'tool' kind can
// now carry `parentToolUseId`: non-null means this slot is showing a
// CHILD's (subagent) tool_use, not the conversation's own. Rendered as
// "Working · 0:14 · Subagent: <label>" (Bot icon) instead of the plain
// "Working · 0:14 · <label>" — same `label` fallback chain either way,
// just an added i18n'd prefix so the founder can tell "I'm doing X" from
// "my helper agent is doing X".

import { useEffect, useState } from 'react';
import { Icon, type IconName } from './atoms/Icon';
import { fallbackHeadline } from '../lib/tool-rendering/verb-map';
import { useTranslation } from '../i18n';
import type { Agent, LiveActivity } from '../types';

export interface LiveActivityProps {
  activity: LiveActivity;
  // Test seam — defaults to Date.now. Vitest fake timers + a custom now
  // give us deterministic timer assertions without flakes.
  nowMs?: () => number;
}

// Format the wall-clock elapsed value as `0:08` / `12:34` / `1h 02m`.
// Sub-second precision is intentionally dropped: a flicker between 0:00
// and 0:01 would feel fragile, and the founder gains zero from it.
export function formatElapsed(elapsedMs: number): string {
  const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

// Resolve a Lucide icon name from a tool's running label. The tracker
// only ships the rendered `label` (not the raw tool name), so we fall
// back to a generic `Wrench` if the label happens to start with one of
// the verb-map prefixes — purely cosmetic, no founder-facing impact.
function iconForToolLabel(label: string): IconName {
  // Cheap prefix lookup for the seven common cases. Anything unknown
  // gets the generic Wrench icon — same as the row would show.
  if (label.startsWith('Opened ')) return 'BookOpen';
  if (label.startsWith('Edited ')) return 'FileEdit';
  if (label.startsWith('Wrote ')) return 'FilePlus';
  if (label.startsWith('Listed ')) return 'Folder';
  if (label.startsWith('Searched the codebase') || label.startsWith('Searched for ')) return 'Search';
  if (label.startsWith('Searched the web') || label.startsWith('Fetched ')) return 'Globe';
  if (label.startsWith('Ran subagent')) return 'Bot';
  if (label.startsWith('Updated plan')) return 'ListChecks';
  if (label.startsWith('Edited a notebook') || label.startsWith('Edited ') && label.endsWith('.ipynb')) return 'Notebook';
  if (label.startsWith('Invoked a skill')) return 'Sparkles';
  if (label.startsWith('Sent a notification')) return 'Bell';
  // Reference: keep `fallbackHeadline` import alive so a future tool
  // registry change forces a typecheck failure here too. Cheap reminder.
  void fallbackHeadline;
  return 'Wrench';
}

// Sprint conditional-wakeups PR-A (spec D7/D8) — maps the D6 structured
// `last_error` code to humanized founder copy. NEVER the raw code
// (founder-first §8.6): unrecognized/future codes fall back to
// "reintentando"/"retrying" rather than leaking machine-shaped text.
function blockedReasonCopy(
  lastError: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  switch (lastError) {
    case 'RUNNER_OFFLINE':
      return t('workingReason.blockedReason.runnerOffline');
    case 'RUNNER_RATE_LIMITED':
      return t('workingReason.blockedReason.runnerRateLimited');
    case 'CAPACITY_EXHAUSTED':
      return t('workingReason.blockedReason.capacityExhausted');
    default:
      return t('workingReason.blockedReason.default');
  }
}

// Sprint working-signal-ssot (spec D6) — persistent fallback activity
// line. Renders when the agent is working but both fine-grained signals
// are silent (no partial text, no LiveActivity): open permission card
// (LIV5 forceIdle), background subagent between turns, scheduled wakeup,
// silent long turn. Same visual family as LiveActivity (pulsing dots +
// caption, existing tokens only); no elapsed timer — these waits have no
// tracked start, and a wrong 0:00 would be worse than none.
/**
 * Campaña agent-noise-collapse (resultado 4) — ¿la conversación está
 * esperando a ayudantes en segundo plano?
 *
 * UN predicado, DOS consumidores: esta línea y la condición de render en
 * `ConversationList`. Sin él, la cola de la conversación se queda MUDA
 * exactamente en el caso que Santi reportó: el padre ya no tiene trabajo
 * propio (`working:false`), los hijos siguen fuera, y el silencio se lee
 * como "se ha colgado" en vez de como "está esperando".
 *
 * `done` queda fuera a propósito: ahí el rollup ya decidió que no hay nada
 * pendiente y la copy correcta es la de "terminó y te espera".
 *
 * CEDE ante `awaiting-wakeup` y `awaiting-permission` (revisor, issue 1).
 * Las dos son alcanzables CON hijos fuera a la vez — un agente que lanzó
 * ayudantes y además registró un wakeup, o que lanzó ayudantes y se topó
 * con una tarjeta de permiso — y las dos dicen algo estrictamente más
 * accionable que "N ayudantes trabajando": la hora a la que retoma (y la
 * línea de entrega bloqueada, conditional-wakeups D8/M7) o que hay una
 * tarjeta esperando al founder. Founder-first §2 prioridad 4: "¿está
 * bloqueado o preguntando algo?" va por delante de "¿está trabajando?".
 */
export function isAwaitingHelpers(
  agent: Pick<Agent, 'founderStatus' | 'workingReason'> | null | undefined,
): boolean {
  const fs = agent?.founderStatus;
  if (!fs || fs.backgroundChildren <= 0) return false;
  if (agent?.workingReason === 'awaiting-wakeup') return false;
  if (agent?.workingReason === 'awaiting-permission') return false;
  return fs.state === 'working' || fs.state === 'unconfirmed';
}

export function WorkingFallback({
  agent,
}: {
  agent: Pick<
    Agent,
    | 'workingReason'
    | 'wakeupFireAt'
    | 'wakeupAttemptCount'
    | 'wakeupLastError'
    // Sprint conditional-wakeups PR-B (spec D8, M7).
    | 'wakeupKind'
    | 'wakeupNote'
    | 'wakeupDeadlineAt'
    // Campaña agent-noise-collapse (resultado 4).
    | 'founderStatus'
  >;
}) {
  const { t } = useTranslation();

  // Campaña agent-noise-collapse (resultado 4) — GANA sobre `workingReason`.
  // El rollup del servidor ya sabe CUÁNTOS ayudantes hay fuera y lo dice en
  // castellano llano ("2 agentes auxiliares están trabajando",
  // `rollup.ts` CHILDREN_COPY / CHILDREN_QUIET_COPY); `workingReason:
  // 'awaiting-task'` sólo sabe decir "un agente en segundo plano", en
  // singular y sin número. Cero copy nueva: el detalle del rollup ES la
  // fuente (contrato §Fuera de alcance).
  if (isAwaitingHelpers(agent)) {
    const fs = agent.founderStatus;
    const detail = fs?.detail;
    // Sin detalle (un cliente viejo, o un rollup que no lo pobló) cae en
    // la copy genérica existente en vez de inventar una.
    const label = detail && detail.trim() !== '' ? detail : t('workingReason.awaitingTask');
    return (
      <div
        className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
        data-testid="working-fallback"
        data-reason="awaiting-helpers"
        // Revisor, issue 4 — `data-reason` describe la RAMA (y tras el
        // guard de `isAwaitingHelpers` la describe con exactitud: aquí no
        // llega ni un wakeup ni un permiso). El estado real del rollup, que
        // es lo que decide la copy Y los puntos, viaja aparte en vez de
        // quedar implícito: `working` y `unconfirmed` renderizan distinto y
        // un test debe poder distinguirlos sin leer el texto.
        data-rollup-state={fs?.state ?? 'unknown'}
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-2 text-caption text-foreground">
          {/* Los puntos pulsan sólo cuando el rollup afirma `working`. En
              `unconfirmed` los hijos llevan rato sin dar señales y una
              animación de "en marcha" sería una mentira animada. */}
          {fs?.state === 'working' && <PulsingDots ariaLabel={t('liveActivity.ariaLabel')} />}
          <span className="truncate max-w-live-activity">{label}</span>
        </span>
      </div>
    );
  }

  let label: string;
  switch (agent.workingReason) {
    case 'awaiting-permission':
      label = t('workingReason.awaitingPermission');
      break;
    case 'awaiting-task':
      label = t('workingReason.awaitingTask');
      break;
    case 'awaiting-wakeup': {
      // Sprint conditional-wakeups PR-B (spec D8, M7) — a conditional row
      // (asoasis-wake) NEVER renders `wakeupFireAt` as a "resumes at"
      // time; it renders `wakeupDeadlineAt` labelled as a limit. This
      // render path does not go through `rollupState` (named here per
      // M7 so it is not missed by a future refactor).
      const isConditional = agent.wakeupKind === 'conditional';
      const rawTs = isConditional ? agent.wakeupDeadlineAt : agent.wakeupFireAt;
      const ts = rawTs ? Date.parse(rawTs) : NaN;
      const timeStr = Number.isFinite(ts)
        ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;
      // PR-A half of D8, restructured post-truncation-finding — a
      // blocked delivery chain (attempt recorded + structured error)
      // REPLACES the generic waiting prefix instead of appending to it.
      // The blocked state is strictly more informative than "esperando
      // una comprobación programada / waiting for a scheduled check";
      // leading with the anomaly is founder-first (§2 priority 4 — "is
      // it blocked or asking something?"). Fits `max-w-live-activity`
      // unchanged — no design-discipline token change. Zero attempts or
      // no recorded error → the clean line, byte-identical to before
      // (except the conditional-vs-timed time source above).
      if ((agent.wakeupAttemptCount ?? 0) > 0 && agent.wakeupLastError) {
        const reason = blockedReasonCopy(agent.wakeupLastError, t);
        const attempt = agent.wakeupAttemptCount ?? 0;
        // Gate-2 fix (M7 BLOCKER) — a CONDITIONAL row's blocked line
        // must label its time "límite"/"limit" (wakeupBlockedLimitAt),
        // never "prevista"/"scheduled" (wakeupBlockedAt): the timestamp
        // here is `wakeupDeadlineAt`, and M7 forbids rendering the
        // deadline as an expected/scheduled time. Timed rows keep
        // `wakeupBlockedAt` — there the time IS a scheduled resume hour.
        if (isConditional) {
          label = timeStr
            ? t('workingReason.wakeupBlockedLimitAt', { reason, attempt, time: timeStr })
            : t('workingReason.wakeupBlocked', { reason, attempt });
        } else {
          label = timeStr
            ? t('workingReason.wakeupBlockedAt', { reason, attempt, time: timeStr })
            : t('workingReason.wakeupBlocked', { reason, attempt });
        }
      } else if (isConditional && agent.wakeupNote && timeStr) {
        // D8: `note` is the ONLY probe-related string ever rendered
        // (never `probe_cmd`, never a raw code). Note absent → falls
        // through to the existing fallback copy below (same strings the
        // timed channel uses, fed with the deadline instead of fire_at).
        label = t('workingReason.awaitingConditionNote', { note: agent.wakeupNote, time: timeStr });
      } else {
        label = timeStr
          ? t('workingReason.awaitingWakeupAt', { time: timeStr })
          : t('workingReason.awaitingWakeup');
      }
      break;
    }
    case 'silent':
      label = t('workingReason.silent');
      break;
    default:
      label = t('workingReason.working');
  }
  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
      data-testid="working-fallback"
      data-reason={agent.workingReason ?? 'working'}
      aria-live="polite"
    >
      <span className="inline-flex items-center gap-2 text-caption text-foreground">
        <PulsingDots ariaLabel={t('liveActivity.ariaLabel')} />
        <span className="truncate max-w-live-activity">{label}</span>
      </span>
    </div>
  );
}

function PulsingDots({ ariaLabel }: { ariaLabel: string }) {
  // Sprint 17 — three semantic utilities (`animate-pulse-dot-1|2|3`)
  // bake the 200ms stagger into the Tailwind animation declaration so
  // the per-dot delay is a token, not an inline style. See
  // `docs/ui/04-tokens.md` §6.1 and `docs/design-system.md` §11.4.
  return (
    <span
      className="inline-flex items-center gap-0.5"
      role="status"
      aria-label={ariaLabel}
      data-testid="live-activity-dots"
    >
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-1"
        aria-hidden="true"
      />
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-2"
        aria-hidden="true"
      />
      <span
        className="inline-block w-1 h-1 rounded-full bg-muted-foreground animate-pulse-dot-3"
        aria-hidden="true"
      />
    </span>
  );
}

export function LiveActivity({ activity, nowMs }: LiveActivityProps) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => (nowMs ? nowMs() : Date.now()));

  // 1Hz tick. One interval shared per mount; the parent ensures only
  // one LiveActivity is mounted at a time.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(nowMs ? nowMs() : Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, [nowMs]);

  const elapsed = formatElapsed(Math.max(0, now - activity.startedAtMs));

  // Sprint ai-elements-rendering (Phase 2, T+X) — restyled as the "active"
  // ChainOfThoughtStep of the work rail (ADR D-026): foreground tone +
  // entrance animation, matching chain-of-thought.tsx `statusStyles.active`.
  // Everything else (dots, icon lookup, 1Hz tick, aria, copy) unchanged.
  if (activity.kind === 'thinking') {
    return (
      <div
        className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
        data-testid="live-activity"
        data-kind="thinking"
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-2 text-caption text-foreground">
          <PulsingDots ariaLabel={t('liveActivity.ariaLabel')} />
          <span>
            {t('liveActivity.thinking')} · {elapsed}
          </span>
        </span>
      </div>
    );
  }

  // Sprint compaction-visibility (D4) — same container as 'thinking':
  // pulsing dots + elapsed time, nothing else can be true concurrently
  // (nothing streams during a compaction; no permission card can be open
  // while the CLI is blocked).
  if (activity.kind === 'compacting') {
    return (
      <div
        className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
        data-testid="live-activity"
        data-kind="compacting"
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-2 text-caption text-foreground">
          <PulsingDots ariaLabel={t('liveActivity.ariaLabel')} />
          <span>
            {t('liveActivity.compacting')} · {elapsed}
          </span>
        </span>
      </div>
    );
  }

  // kind === 'tool'
  //
  // Sprint d028-recursive-liveness PR-C (D5, EC-176) — `parentToolUseId`
  // set means this slot is showing a CHILD's (subagent) tool_use, not the
  // conversation's own. Render it as a labelled helper-agent line — an
  // i18n'd prefix plus the same verbatim `description` fallback chain the
  // parent's own row already uses — so the founder can tell "I am doing
  // X" from "my helper agent is doing X" without ever seeing a tool name
  // or a tool_use_id (founder-first §8.6).
  //
  // Gate 2 (reviewer-lifecycle) BLOCKER, fixed: `!== null` treated an
  // ABSENT field the same as a labelled one (`undefined !== null` is
  // `true`). The skew window this guards against is REAL, not hypothetical.
  // Until 2026-09-08 the SPA (Cloudflare Pages) and the control plane were
  // INDEPENDENT publishes with no ordering guarantee. Since zt4b (INV-19)
  // both are built and reloaded by the same deploy on the same host, which
  // NARROWS that window but does not close it: a browser can still be holding
  // a cached bundle from before the reload. Keep the guard — every
  // `kind: 'tool'` payload omits the field until the
  // control plane deploys, and the old truthy check rendered every own
  // tool on every conversation as "Subagent: <label>" fleet-wide for the
  // whole skew window. Explicit truthy-non-empty-string check treats
  // absent and `null` identically — both mean "the conversation's own".
  const isSubagentActivity =
    typeof activity.parentToolUseId === 'string' && activity.parentToolUseId.length > 0;
  const displayLabel = isSubagentActivity
    ? t('liveActivity.subagentLabel', { label: activity.label })
    : activity.label;
  return (
    <div
      className="fade-in-0 slide-in-from-top-2 animate-in flex justify-start py-1"
      data-testid="live-activity"
      data-kind="tool"
      data-subagent={isSubagentActivity ? 'true' : undefined}
      aria-live="polite"
    >
      <span className="inline-flex items-center gap-2 text-caption text-foreground">
        <Icon
          name={isSubagentActivity ? 'Bot' : iconForToolLabel(activity.label)}
          size={14}
          className="shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
        <span className="truncate max-w-live-activity">
          {t('liveActivity.working')} · {elapsed} · {displayLabel}
        </span>
      </span>
    </div>
  );
}
